import { useEffect, useId, useRef } from 'react';
import { useWidget } from '../context/WidgetContext.js';
import { IconClose } from '../components/icons.js';
import type { LightboxPayload } from './types.js';

type MediaLightboxProps = {
  payload: LightboxPayload;
  onClose: () => void;
};

export function MediaLightbox({ payload, onClose }: MediaLightboxProps) {
  const { t } = useWidget();
  const titleId = useId();
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const title =
    payload.title ??
    (payload.kind === 'embed' ? payload.title : undefined);

  return (
    <div
      className="teo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      onClick={onClose}
    >
      <div
        className="teo-lightbox__panel"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="teo-lightbox__close teo-icon-btn"
          onClick={onClose}
          aria-label={t.mediaClosePreview}
        >
          <IconClose size={20} />
        </button>

        {payload.kind === 'image' && (
          <img
            className="teo-lightbox__image"
            src={payload.url}
            alt={title ?? ''}
          />
        )}

        {payload.kind === 'video' && (
          <video
            className="teo-lightbox__video"
            src={payload.url}
            controls
            autoPlay
            playsInline
          />
        )}

        {payload.kind === 'embed' && (
          <iframe
            className="teo-lightbox__embed"
            src={payload.embedUrl}
            title={title ?? t.mediaOpenPreview}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}

        {title && (
          <p id={titleId} className="teo-lightbox__caption">
            {title}
          </p>
        )}
      </div>
    </div>
  );
}
